import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Ticket, Link, Plus, Trash, Copy, Check, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [inviteLinks, setInviteLinks] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [newDuration, setNewDuration] = useState('1month');
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [inviteDuration, setInviteDuration] = useState('1month');
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [codesRes, linksRes] = await Promise.all([
        axios.get(`${API}/admin/promo-codes`, { withCredentials: true }),
        axios.get(`${API}/admin/invite-links`, { withCredentials: true })
      ]);
      setPromoCodes(codesRes.data);
      setInviteLinks(linksRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error.message);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createPromo = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) { toast.error('Entrez un code'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, { code: newCode, duration: newDuration, max_uses: newMaxUses }, { withCredentials: true });
      toast.success(`Code "${newCode.toUpperCase()}" créé !`);
      setNewCode('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const createInvite = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/invite-links`, { duration: inviteDuration }, { withCredentials: true });
      toast.success("Lien d'invitation créé !");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const deletePromo = async (code) => {
    try { await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true }); setPromoCodes(p => p.filter(c => c.code !== code)); toast.success('Supprimé'); }
    catch (error) { console.error("Delete promo error:", error.message); toast.error('Erreur'); }
  };

  const deleteInvite = async (code) => {
    try { await axios.delete(`${API}/admin/invite-links/${code}`, { withCredentials: true }); setInviteLinks(l => l.filter(c => c.code !== code)); toast.success('Supprimé'); }
    catch (error) { console.error("Delete invite error:", error.message); toast.error('Erreur'); }
  };

  const copyInviteUrl = async (code) => {
    const url = `${window.location.origin}?invite=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(code);
      toast.success('Lien copié !');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) { console.error("Copy failed:", error.message); toast.error('Impossible de copier'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-panel">
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Ticket size={20} weight="duotone" className="text-[#A78BFA]" />
          <h3 className="font-bold text-base text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Codes promo</h3>
        </div>
        <form onSubmit={createPromo} className="flex flex-wrap gap-2 mb-4">
          <input data-testid="promo-code-input" value={newCode} onChange={(e) => setNewCode(e.target.value)}
            placeholder="Code (ex: AMIBTP)" className="h-10 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm flex-1 min-w-[140px] focus:border-[#8B5CF6] outline-none placeholder:text-[#71717A]" />
          <select value={newDuration} onChange={(e) => setNewDuration(e.target.value)} data-testid="promo-duration-select"
            className="h-10 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none">
            <option value="1month">1 mois</option><option value="3months">3 mois</option><option value="unlimited">Illimité</option>
          </select>
          <input type="number" min="1" max="999" value={newMaxUses} onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 1)}
            className="h-10 w-20 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none" data-testid="promo-max-uses" />
          <button type="submit" disabled={loading} data-testid="create-promo-btn"
            className="h-10 px-4 bg-[#8B5CF6] text-white rounded-lg font-medium text-sm hover:bg-[#7C3AED] flex items-center gap-1.5 disabled:opacity-60">
            <Plus size={16} weight="bold" /> Créer
          </button>
        </form>
        {promoCodes.length === 0 ? (
          <p className="text-sm text-[#71717A]">Aucun code promo</p>
        ) : (
          <div className="space-y-2">
            {promoCodes.map(c => (
              <div key={c.code} className="flex items-center justify-between px-3 py-2 bg-[#09090B] rounded-lg border border-[#27272A]" data-testid={`promo-${c.code}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-sm text-[#A78BFA]">{c.code}</span>
                  <span className="text-xs text-[#71717A] bg-[#27272A] px-2 py-0.5 rounded">{c.duration_label}</span>
                  <span className="text-xs text-[#71717A]">{c.uses}/{c.max_uses} utilisé(s)</span>
                </div>
                <button onClick={() => deletePromo(c.code)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-promo-${c.code}`}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Link size={20} weight="duotone" className="text-[#A78BFA]" />
          <h3 className="font-bold text-base text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Liens d'invitation</h3>
        </div>
        <div className="flex gap-2 mb-4">
          <select value={inviteDuration} onChange={(e) => setInviteDuration(e.target.value)} data-testid="invite-duration-select"
            className="h-10 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none">
            <option value="1month">1 mois gratuit</option><option value="3months">3 mois gratuits</option><option value="unlimited">Illimité</option>
          </select>
          <button onClick={createInvite} disabled={loading} data-testid="create-invite-btn"
            className="h-10 px-4 bg-[#8B5CF6] text-white rounded-lg font-medium text-sm hover:bg-[#7C3AED] flex items-center gap-1.5 disabled:opacity-60">
            {loading ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />} Générer un lien
          </button>
        </div>
        {inviteLinks.length === 0 ? (
          <p className="text-sm text-[#71717A]">Aucun lien d'invitation</p>
        ) : (
          <div className="space-y-2">
            {inviteLinks.map(l => (
              <div key={l.code} className="flex items-center justify-between px-3 py-2 bg-[#09090B] rounded-lg border border-[#27272A]" data-testid={`invite-${l.code}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-bold text-sm text-[#A78BFA]">{l.code}</span>
                  <span className="text-xs text-[#71717A] bg-[#27272A] px-2 py-0.5 rounded">{l.duration_label}</span>
                  <span className="text-xs text-[#71717A]">{l.uses}/{l.max_uses} utilisé(s)</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => copyInviteUrl(l.code)} data-testid={`copy-invite-${l.code}`}
                    className={`p-1.5 rounded-lg transition-all ${copiedId === l.code ? 'text-green-400' : 'text-[#A1A1AA] hover:text-[#F4F4F5] hover:bg-[#27272A]'}`}>
                    {copiedId === l.code ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                  <button onClick={() => deleteInvite(l.code)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-invite-${l.code}`}><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
